// #354, brief §15 row 30: a model from the athlete's own rows. Each slot's
// rule is read back (recognizeRule) and moved onto an axis a model may use
// (MODEL_AXES): kilos become % of the author's MAX, RIR becomes RPE. The input
// is days of slots, so a program today and a logged session later both fit.
// Keep free of React and any UI import.
import type { Locale } from "./locale.js";
import type { LocalizedText } from "./localizedText.js";
import type { SlotDraft } from "./programEdit.js";
import type { ModelDay, ModelLevel, ProgramModel } from "./programModel.js";
import { mapRuleValues, ruleToRpe } from "./programModelDraft.js";
import { readRound, type ProgressionRule } from "./programProgression.js";
import { recognizeRule } from "./programProgressionRecognize.js";
import { pctOfMax } from "./resolveDefaultWeight.js";
import type { RoundType } from "./schemas/programV3.js";

export type ModelSourceDay = { name: string; slots: readonly SlotDraft[] };

export type ModelSource = {
  name: string;
  /** Never empty: the model list shows it under the name. */
  description: string;
  /** The language the texts are written in; null when nobody said. */
  locale: Locale | null;
  roundTypes: readonly RoundType[];
  sessionsPerWeek: number;
  days: readonly ModelSourceDay[];
  /** The author's current MAX by exercise, in the slots' load unit. */
  maxes: ReadonlyMap<string, number>;
  /** Exercises whose MAX is the total, body included (catalog isBodyweight):
   *  their load is the extra, so a share of the MAX would be wrong. */
  bodyweight: ReadonlySet<string>;
  /** The author's load step, in the slots' unit (loadStepFor). */
  loadQuantum: number;
};

/** A source row, by place. */
export type ModelSourceRow = { day: number; slot: number; exerciseId: string };

/** A row the model holds differently: cells by hand it does not keep, or
 *  rounds it skipped that it now runs. */
export type ModelChangedRow = ModelSourceRow & { byHand: boolean; everyRound: boolean };

export type ModelFromSource = {
  /** The id is the server's (POST /api/templates/models?scope=mine). */
  model: Omit<ProgramModel, "id">;
  /** Load rows with no MAX, or a bodyweight extra: on history now, the load
   *  no longer climbs. */
  toHistory: ModelSourceRow[];
  /** Rows the recognizer cannot read: sets and reps only, no load. */
  noLoad: ModelSourceRow[];
  changed: ModelChangedRow[];
};

/** One program, one level: the middle one, the name claims no more. */
const SOURCE_LEVEL: ModelLevel = "intermediate";

/** LocalizedText needs English: the author's words go there too. */
const textIn = (text: string, locale: Locale | null): LocalizedText => {
  const out: LocalizedText = { en: text };
  if (locale && locale !== "en") out[locale] = text;
  return out;
};

/** A share of the MAX, values to 0.1 %. Steps go to 0.01 %: rounded as
 *  coarsely as the start, they would drift a round at a time. */
const pctOf = (max: number, grain: number) => (load: number) =>
  Math.round(pctOfMax(load, max) * grain) / grain;

/** A load rule with no MAX to read it against: reps, sets and effects on the
 *  set count stay, every load number goes. */
const onHistory = (rule: ProgressionRule): ProgressionRule => {
  const { perSet, threshold, test, deload } = rule;
  const out: ProgressionRule = { ...rule, axis: "history", value: null };
  delete out.perSet;
  if (perSet?.reps) out.perSet = { reps: perSet.reps };
  if (threshold) out.threshold = { ...threshold, loadStart: 0, loadStep: 0 };
  if (test) out.test = test.amrapLast ? { amrapLast: true, ...(test.amrapStop ? { amrapStop: test.amrapStop } : {}) } : {};
  if (deload) out.deload = deload.setsDelta ? { setsDelta: deload.setsDelta } : {};
  return out;
};

/** A row read as «valori a mano»: the sets and reps of its first round. */
const manualRule = (slot: SlotDraft): ProgressionRule => {
  const timed = slot.setRows[0]?.workKind === "countdown";
  let sets: ReturnType<typeof readRound> = null;
  for (let w = 0; w < slot.inactiveWeeks.length && !sets?.length; w += 1) sets = readRound(slot, w, timed);
  const reps = sets?.find((s) => s.reps !== null)?.reps ?? 0;
  return {
    axis: "none",
    ...(timed ? { work: "countdown" as const } : {}),
    sets: Math.max(1, sets?.length ?? 0),
    reps: { kind: "constant", value: reps },
    value: null,
  };
};

type RowRule = { rule: ProgressionRule; lost?: "history" | "noLoad"; byHand: boolean };

const rowRule = (slot: SlotDraft, src: ModelSource): RowRule => {
  const rec = recognizeRule(slot, src.roundTypes, { loadQuantum: src.loadQuantum });
  if (rec.kind === "manual") return { rule: manualRule(slot), lost: "noLoad", byHand: false };
  const byHand = rec.exceptions.length > 0;
  const rule = ruleToRpe(rec.rule);
  if (rule.axis !== "absolute") return { rule, byHand };
  const max = src.maxes.get(slot.exerciseId);
  if (max === undefined || max <= 0 || src.bodyweight.has(slot.exerciseId))
    return { rule: onHistory(rule), lost: "history", byHand };
  return { rule: mapRuleValues(rule, "percent-of-max", pctOf(max, 10), pctOf(max, 100)), byHand };
};

/** A model from `src`: one level, one block, every row a main one with one
 *  exercise. Slots with no exercise are left out, and so are days left empty. */
export const modelFromSource = (src: ModelSource): ModelFromSource => {
  const toHistory: ModelSourceRow[] = [];
  const noLoad: ModelSourceRow[] = [];
  const changed: ModelChangedRow[] = [];
  let n = 0;
  const days: ModelDay[] = [];
  src.days.forEach((day, d) => {
    const rows: ModelDay["rows"] = [];
    day.slots.forEach((slot, s) => {
      if (!slot.exerciseId) return;
      const { rule, lost, byHand } = rowRule(slot, src);
      const ref = { day: d, slot: s, exerciseId: slot.exerciseId };
      if (lost === "history") toHistory.push(ref);
      if (lost === "noLoad") noLoad.push(ref);
      // A model row runs every round: the skipped ones are lost.
      const everyRound = slot.inactiveWeeks.slice(0, src.roundTypes.length).some(Boolean);
      if (byHand || everyRound) changed.push({ ...ref, byHand, everyRound });
      n += 1;
      rows.push({ id: `r${n}`, weight: "main", byLevel: { [SOURCE_LEVEL]: { exercises: [slot.exerciseId], rule } } });
    });
    if (rows.length === 0) return;
    const name = day.name.trim() || `D${d + 1}`;
    days.push({ id: `d${d + 1}`, name: textIn(name, src.locale), rows });
  });
  return {
    model: {
      name: textIn(src.name, src.locale),
      description: textIn(src.description, src.locale),
      levels: [SOURCE_LEVEL],
      roundPattern: [...src.roundTypes],
      blocks: [1],
      sessionsPerWeek: [src.sessionsPerWeek],
      days,
    },
    toHistory,
    noLoad,
    changed,
  };
};
